/**
 * Speaker button component for playing audio responses
 *
 * Shows a prominent "Speaking..." indicator when playing with cancel option
 */
import React from 'react';

interface SpeakerButtonProps {
  isPlaying: boolean;
  isLoading?: boolean;
  onClick: () => void;
  disabled?: boolean;
}

export const SpeakerButton: React.FC<SpeakerButtonProps> = ({
  isPlaying,
  isLoading = false,
  onClick,
  disabled = false,
}) => {
  // When playing, show prominent "Speaking..." indicator with separate cancel button
  if (isPlaying && !isLoading) {
    return (
      <div className="flex items-center gap-2">
        {/* Speaking indicator */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500 text-white">
          {/* Sound wave animation */}
          <div className="flex items-center gap-0.5">
            <div className="w-1 h-3 bg-white rounded-full animate-bounce" style={{ animationDuration: '0.5s' }}></div>
            <div className="w-1 h-4 bg-white rounded-full animate-bounce" style={{ animationDuration: '0.5s', animationDelay: '0.1s' }}></div>
            <div className="w-1 h-2 bg-white rounded-full animate-bounce" style={{ animationDuration: '0.5s', animationDelay: '0.2s' }}></div>
          </div>
          <span className="text-sm font-medium">Speaking...</span>
        </div>

        {/* Separate Cancel/Stop button */}
        <button
          onClick={onClick}
          className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-all duration-200 cursor-pointer"
          title="Stop speaking"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        </button>
      </div>
    );
  }

  // When loading audio, show loading indicator
  if (isLoading) {
    return (
      <button
        disabled
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-200 text-gray-600 cursor-wait"
        title="Loading audio..."
      >
        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span className="text-sm">Loading...</span>
      </button>
    );
  }

  // Default state - speaker icon button
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        p-2 rounded-full transition-all duration-200
        bg-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-100
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      title="Play audio"
    >
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
      </svg>
    </button>
  );
};
